<template>
  <main :aria-label="$t('general.all_contents')" class="contents-list-page">
    <CompanyBreadcrumb :aria-label="$t('general.all_contents')" :home-aria-label="$t('general.home')"
      :items="breadcrumbItems" />

    <template v-if="errorMessages">
      <div v-for="(error, index) in errorMessages" :key="index" class="card contents-alert-card">
        <div class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close />
          <p><i class="uil-exclamation-triangle" /> {{ error }}</p>
        </div>
      </div>
    </template>

    <div v-if="successMessage" class="card contents-alert-card">
      <div class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close />
        <p><i class="uil-check-circle" /> {{ successMessage }}</p>
      </div>
    </div>

    <div class="contents-page-header">
      <div class="contents-page-header-left">
        <h2 class="contents-page-title">
          {{ $t("general.all_contents") }} ({{ items.total }})
        </h2>
      </div>
      <div class="contents-page-header-right">
        <router-link v-if="$hasPermission('create_content')" :to="{ name: 'company.content.add' }"
          class="btn btn-default contents-add-btn">
          <i class="uil-presentation-plus" /> {{ $t("general.add_content") }}
        </router-link>
        <button v-else :style="{ cursor: 'not-allowed' }" class="btn btn-default contents-add-btn" disabled>
          <i class="uil-presentation-plus" /> {{ $t("general.add_content") }}
        </button>
      </div>
    </div>

    <div class="contents-toolbar">
      <div class="contents-toolbar-search">
        <search-input v-model="search" :aria-label="$t('general.search_in_contents')" :control-height="48" :debounce="0"
          :placeholder="$t('general.search_in_contents')" />
      </div>
      <div class="contents-toolbar-actions">
        <sort-dropdown :sort-options="sortOptions" @sort-selected="sortOrder" />
        <button :aria-label="$t('general.filter')" :class="{
          'contents-filter-btn--active': filterVisible || hasActiveFilters,
        }" :uk-tooltip="$t('general.filter')" class="btn btn-icon btn-hover btn-circle contents-filter-btn"
          type="button" @click="filterVisible = !filterVisible">
          <i class="uil-filter"></i>
        </button>
      </div>
    </div>

    <div class="contents-layout">
      <div class="contents-main">
        <default-loading v-if="content_loading"></default-loading>
        <template v-else>
          <div class="contents-grid">
            <article v-for="(item, index) in formatteditems" :key="item.id || index" class="card contents-card">
              <img :alt="item.title" :src="item.photo_link" class="contents-card-cover" />
              <div class="contents-card-body">
                <h3 class="contents-card-title">{{ item.title }}</h3>
                <span class="contents-card-meta">{{ $t(item.key_name) }}</span>
                <span class="contents-card-meta">{{ item.category_name }}</span>
                <span class="contents-card-meta">{{ item.created_at }}</span>
              </div>
              <div class="contents-card-actions">
                <router-link v-if="$hasPermission('create_assignment')"
                  :aria-label="$t('menu.company_dashboard.create_assignment')" :to="{
                    name: 'company.assignments.add',
                    params: {
                      id: item.id,
                      title: item.title,
                      name: item.name,
                    },
                  }" class="btn btn-info btn-sm contents-card-btn">
                  <i class="uil-location-arrow"></i>
                  <span class="contents-card-btn-label">{{
                    $t("menu.company_dashboard.create_assignment")
                  }}</span>
                </router-link>
                <button v-else :aria-label="$t('general.no_permission')" :style="{ cursor: 'not-allowed', opacity: 0.5 }"
                  :uk-tooltip="$t('general.no_permission')" class="btn btn-info btn-sm contents-card-btn" disabled>
                  <i class="uil-location-arrow"></i>
                  <span class="contents-card-btn-label">{{
                    $t("menu.company_dashboard.create_assignment")
                  }}</span>
                </button>

                <button :aria-label="$t('general.preview')" class="btn btn-outline-secondary btn-sm contents-card-btn"
                  type="button" @click="openPreviewModal(item)">
                  <i class="uil-presentation-play" />
                  <span class="contents-card-btn-label">{{
                    $t("general.preview")
                  }}</span>
                </button>

                <router-link :aria-label="$hasPermission('create_content')
                      ? $t('general.edit')
                      : $t('general.no_permission')
                    " :style="{
      cursor: $hasPermission('create_content')
        ? 'pointer'
        : 'not-allowed',
      pointerEvents: $hasPermission('create_content')
        ? 'auto'
        : 'none',
      opacity: $hasPermission('create_content') ? 1 : 0.5,
    }" :to="$hasPermission('create_content')
  ? {
    name: 'company.contents.edit',
    params: { id: item.id },
  }
  : '#'
  " class="btn btn-secondary btn-sm contents-card-btn" v-bind="!$hasPermission('create_content')
    ? { 'uk-tooltip': $t('general.no_permission') }
    : {}
    ">
                  <i class="uil-cog"></i>
                  <span class="contents-card-btn-label">{{
                    $t("general.edit")
                  }}</span>
                </router-link>

                <button v-if="$hasPermission('delete_content')" class="btn btn-secondary btn-sm mb-3"
                  @click.prevent="openDeleteModal(item.id)" :style="{
                    cursor: 'pointer',
                    opacity: 1,
                  }">
                  <i class="uil-minus-circle"></i> {{ $t("general.delete") }}
                </button>
                <button v-else :aria-label="$t('general.no_permission')" :style="{ cursor: 'not-allowed', opacity: 0.5 }"
                  :uk-tooltip="$t('general.no_permission')" class="btn btn-secondary btn-sm contents-card-btn" disabled>
                  <i class="uil-minus-circle"></i>
                  <span class="contents-card-btn-label">{{
                    $t("general.delete")
                  }}</span>
                </button>
              </div>
            </article>
          </div>

          <div class="contents-pagination">
            <pagination v-if="pagination && pagination.hasOwnProperty('links')" :pagination="pagination"
              @changePage="setPage($event)" />
          </div>
        </template>
      </div>

      <aside :class="{ 'sidebar-filter-visible': filterVisible }" class="sidebar-filter card contents-sidebar">
        <div class="sidebar-filter-contents contents-filter">
          <h4 class="contents-filter-title">
            <i class="uil-filter" /> {{ $t("general.filter") }}
          </h4>
          <label class="contents-filter-option">
            <input :data-id="'all'" checked class="uk-radio" data-type="all" name="filters" type="radio"
              @change="setType" />
            <span>{{ $t("general.all") }}</span>
          </label>

          <ul class="sidebar-filter-list uk-accordion" uk-accordion="multiple: true">
            <li class="uk-open">
              <a class="uk-accordion-title contents-filter-accordion" href="#">
                {{ $t("general.by_content_type") }}
              </a>
              <div class="uk-accordion-content contents-filter-panel">
                <div class="uk-form-controls">
                  <label v-for="(item, indis) in formattedfilters.file_type" v-if="item.contents_count > 0"
                    :key="`filters-0-${indis}`" class="contents-filter-option">
                    <input :data-id="item.id" class="uk-radio" data-type="file_type_id" name="filters" type="radio"
                      @change="setType" />
                    <span>
                      {{ $t(item.key_name) }}
                      <span>({{ item.contents_count }})</span>
                    </span>
                  </label>
                  <label v-for="(item, i) in formattedfilters.content_type" v-if="item.contents_count > 0"
                    :key="`filters-1-${i}`" class="contents-filter-option">
                    <input :data-id="item.id" class="uk-radio" data-type="content_type_id" name="filters" type="radio"
                      @change="setType" />
                    <span>
                      {{ $t("single." + item.key_name) }}
                      <span>({{ item.contents_count }})</span>
                    </span>
                  </label>
                </div>
              </div>
            </li>

            <li class="uk-open">
              <a class="uk-accordion-title contents-filter-accordion" href="#">
                {{ $t("general.by_category_tag") }}
              </a>
              <div class="uk-accordion-content">
                <div class="contents-tag-search">
                  <label class="contents-tag-search-label" for="search-tag">
                    <i class="uil-search"></i>
                    {{ $t("general.tag_search") }}
                  </label>
                  <input id="search-tag" v-model="searchTag" :placeholder="$t('general.type_a_tag')" autocomplete="off"
                    class="form-control contents-tag-search-input" type="text" />
                </div>
                <div class="uk-form-controls contents-tag-list">
                  <label v-for="(item, index) in filteredTags" :key="`filteredTags-${index}`"
                    class="contents-filter-option">
                    <input :data-id="item.name" class="uk-radio" data-type="hashtag" name="filters" type="radio"
                      @change="setType" />
                    <span>{{ item.name }}</span>
                  </label>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </aside>
    </div>

    <content-preview-modal ref="contentPreviewModal" :preview-content="previewContentData"
      :selected-content="selectedContent" />
    <!-- Delete Content Modal -->
    <div ref="deleteContentModal" id="deleteContentModal" uk-modal uk-flex uk-flex-center uk-flex-middle>
      <div class="uk-modal-dialog uk-modal-body" style="max-width: 550px; border-radius: 8px; padding: 30px; top: 16rem">
        <h3 class="uk-modal-title mb-4 text-center">
          {{ $t("general.sure_to_delete") }}
        </h3>

        <div class="d-flex justify-content-center align-items-center">
          <button class="btn btn-danger mr-3" type="button" @click="permanentlyDeleteItem" style="padding: 10px 22px">
            <i class="fa fa-trash mr-2"></i>
            {{ $t("general.yes_delete_it") }}
          </button>
          <button class="btn btn-warning mr-3" type="button" @click="archiveItem" style="padding: 10px 22px">
            <i class="fa fa-archive mr-2"></i>
            Arşive Al
          </button>
          <button class="btn btn-secondary uk-modal-close" type="button" style="padding: 10px 22px">
            {{ $t("general.cancel") }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import store from "@/core/services";
import moment from "moment";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import Pagination from "@/assets/components/Pagination";
import ContentPreviewModal from "@/view/components/modal/ContentPreviewModal";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import SearchInput from "@/view/components/inputs/SearchInput.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import module, {
  BASE_URL,
  DELETE_ITEM,
  ERROR,
  FILTERS,
  FILTERS_URL,
  GET_FILTERS,
  GET_ITEMS,
  ITEMS,
  LOADING,
  MODULE_NAME,
} from "@/core/services/store/content.module";
import axios from "axios";
import { confirmDialogDelete } from "@/utils/confirmDialog";

export default {
  name: "ContentList",
  components: {
    CompanyBreadcrumb,
    DefaultLoading,
    Pagination,
    ContentPreviewModal,
    SearchInput,
    SortDropdown,
  },
  data() {
    return {
      page: 1,
      per_page: 2,
      successMessage: null,
      errorMessages: [],
      search: "",
      searchTag: "",
      filterVisible: false,
      filter: {
        orderBy: { id: "created_at", name: this.$t("filter.Filter_by_date") },
        sort: null,
        hashtag: null,
        file_type_id: null,
        content_type_id: null,
      },
      pagination: null,
      searchTagList: [],
      selectedContent: null,
      previewContentData: null,
      deleteContentId: null,
      isDeleting: false,
      isArchiving: false,
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
  },
  computed: {
    breadcrumbItems() {
      return [{ name: this.$t("general.all_contents") }];
    },
    sortOptions() {
      return [
        {
          key: "title-asc",
          sort: "title",
          order: "asc",
          label: this.$t("filters.order.a_z"),
        },
        {
          key: "title-desc",
          sort: "title",
          order: "desc",
          label: this.$t("filters.order.z_a"),
        },
        {
          key: "category-asc",
          sort: "category",
          order: "asc",
          label: this.$t("filter.Filter_by_category"),
        },
        {
          key: "created_at-asc",
          sort: "created_at",
          order: "asc",
          label: this.$t("filters.order.created_at_asc"),
        },
        {
          key: "created_at-desc",
          sort: "created_at",
          order: "desc",
          label: this.$t("filters.order.created_at_desc"),
        },
      ];
    },
    hasActiveFilters() {
      return !!(
        this.filter.hashtag ||
        this.filter.file_type_id ||
        this.filter.content_type_id
      );
    },
    filters: {
      get() {
        return store.getters[MODULE_NAME + "/" + FILTERS];
      },
      set(value) { },
    },
    content_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) { },
    },
    filteredTags() {
      const tags = this.searchTagList;
      const search = this.searchTag;
      if (search === "" || tags == null) return tags;

      return tags.filter((tag) => {
        return tag.name.toLowerCase().includes(search.toLowerCase());
      });
    },
    items: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEMS];
      },
      set(value) { },
    },
    formattedfilters: {
      get() {
        let filters = this.filters;
        if (filters === null) return [];
        return filters;
      },
    },
    formatteditems: {
      get() {
        let results = [];
        let items = this.items.data;

        this.pagination = this.items;

        if (items === null || undefined == items) return [];
        for (let i = 0; i < items.length; i++) {
          let currentItem = items[i];
          if (currentItem) {
            results.push({
              title: currentItem.title,
              name: currentItem.name,
              category_name: currentItem.category_name,
              id: currentItem.id,
              key_name: currentItem.key_name,
              photo_link: currentItem.cover_photo_link,
              created_at: moment(currentItem.created_at).format(
                "DD/MM/YYYY HH:mm:ss"
              ),
              assignments: currentItem.assignments_count > 0 ? true : false,
            });
          }
        }
        return results;
      },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set(value) { },
    },
  },
  methods: {

    openDeleteModal(id) {
      if (this.isDeleting || this.isArchiving) {
        return;
      }

      this.deleteContentId = id;

      this.$nextTick(() => {
        if (this.$refs.deleteContentModal) {
          UIkit.modal(this.$refs.deleteContentModal).show();
        }
      });
    },
    async archiveItem() {
      if (
        !this.deleteContentId ||
        this.isArchiving ||
        this.isDeleting
      ) {
        return;
      }

      this.isArchiving = true;

      try {
        await axios.post(
          `/api/content/${this.deleteContentId}/archive`
        );

        this.successMessage = "Başarıyla arşive alındı";
        this.filterResult();
      } catch (error) {
        this.errorMessages.push(
          error.response?.data?.message || error.message
        );
      } finally {
        this.isArchiving = false;

        if (this.$refs.deleteContentModal) {
          UIkit.modal(this.$refs.deleteContentModal).hide();
        }

        this.deleteContentId = null;
      }
    },
    async permanentlyDeleteItem() {
      if (
        !this.deleteContentId ||
        this.isDeleting ||
        this.isArchiving
      ) {
        return;
      }

      this.isDeleting = true;
      try {
        await axios.delete(
          `/api/content/${this.deleteContentId}`
        );

        this.successMessage = this.$t(
          "general.successfully_deleted"
        );

        this.filterResult();
      } catch (error) {
        this.errorMessages.push(
          error.response?.data?.message || error.message
        );
      } finally {
        this.isDeleting = false;

        if (this.$refs.deleteContentModal) {
          UIkit.modal(this.$refs.deleteContentModal).hide();
        }

        this.deleteContentId = null;
      }
    },
    filterResult() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: {
          is_archived: 0,
          search: this.search,
          page: this.page,
          per_page: this.per_page,
          orderBy: this.filter.orderBy.id,
          sort: this.filter.sort,
          hashtag: this.filter.hashtag,
          file_type_id: this.filter.file_type_id,
          content_type_id: this.filter.content_type_id,
        },
      });
    },
    sortOrder({ sort, order }) {
      this.page = 1;
      this.filter.orderBy = { id: sort, name: sort };
      this.filter.sort = order;
      this.filterResult();
    },
    setType(event) {
      let target = event.target;
      let id = target.getAttribute("data-id");
      let type = target.getAttribute("data-type");
      this.filter.file_type_id =
        this.filter.content_type_id =
        this.filter.hashtag =
        null;
      if (type == "hashtag") this.filter.hashtag = id;
      else if (type == "file_type_id") this.filter.file_type_id = id;
      else if (type == "content_type_id") this.filter.content_type_id = id;
      else if (type === "all") {
        this.getItems();
      }
      type !== "all" ? this.debouncedFilterType() : null;
    },
    setPage(page) {
      this.page = page;
      this.filterResult();
    },
    getContentFilters() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_FILTERS, {
        url: FILTERS_URL,
      });
    },
    async getFilteredTags() {
      const { data } = await axios.get(`/api/active-tags`);
      this.searchTagList = data;
    },
    getItems() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: { is_archived: 0 },
      });
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
    async openPreviewModal(item) {
      try {
        const url = `/api/content/preview?content_id=${item.id}`;
        const response = await axios.get(url);

        let previewContent = null;

        if (response.data) {
          if (response.data.data) {
            previewContent = response.data.data;
          }
        }

        if (previewContent && this.$refs.contentPreviewModal) {
          this.selectedContent = item;
          this.previewContentData = previewContent;

          this.$refs.contentPreviewModal.showModal();
        } else {
          console.error("No preview content or modal ref not found!");
        }
      } catch (error) {
        console.error("Error fetching preview data:", error);
      }
    },
  },
  mounted() {
    this.getContentFilters();
    this.getFilteredTags();
    this.getItems();
  },
  watch: {
    search: function () {
      this.debouncedFilterSearch();
    },
    successMessage: function () {
      this.debouncedResetValues();
    },
    errorMessages: function () {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.filterResult, 500);
    this.debouncedFilterType = _.debounce(this.filterResult, 300);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>

<style scoped src="./ContentList.css" />
